// client/src/components/DeletedUsers.js

import React, { useEffect, useState } from 'react';
import axiosInstance from '../axiosInstance';
import { Typography, Table, TableBody, TableCell, TableHead, TableRow, IconButton, Tooltip, Alert, Box, Button } from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useNavigate } from 'react-router-dom';
import AppBarHeader from './AppBarHeader';
import { jwtDecode } from 'jwt-decode';

function DeletedUsers() {
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const user = token ? jwtDecode(token) : null;
  const userId = user ? user.id : null;
  const isAdmin = user ? user.isAdmin : false;

  const fetchDeletedUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axiosInstance.get('/admin/users/deleted', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeletedUsers(response.data);
    } catch (error) {
      console.error('Error fetching deleted users:', error);
      setError('Access denied or an error occurred.');
      if (error.response && error.response.status === 403) {
        navigate('/dashboard');
      }
    }
  };

  useEffect(() => {
    // Check if user is admin, if not redirect to dashboard
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    
    fetchDeletedUsers();
  }, [navigate, isAdmin]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleRestoreUser = async (userId) => {
    if (window.confirm('Are you sure you want to restore this user? The user will be visible in all views again.')) {
      try {
        const token = localStorage.getItem('token');
        await axiosInstance.put(`/admin/users/${userId}/restore`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // Refresh the deleted users list
        fetchDeletedUsers();
      } catch (error) {
        console.error('Error restoring user:', error);
        if (error.response && error.response.data && error.response.data.error) {
          alert(error.response.data.error);
        } else {
          alert('An error occurred while restoring the user');
        }
      }
    }
  };

  const handlePermanentDelete = async (userId) => {
    if (window.confirm('WARNING: This action cannot be undone. Are you sure you want to permanently delete this user and all their data?')) {
      try {
        const token = localStorage.getItem('token');
        await axiosInstance.delete(`/admin/users/${userId}/permanent`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // Refresh the deleted users list
        fetchDeletedUsers();
      } catch (error) {
        console.error('Error permanently deleting user:', error);
        if (error.response && error.response.data && error.response.data.error) {
          alert(error.response.data.error);
        } else {
          alert('An error occurred while permanently deleting the user');
        }
      }
    }
  };

  return (
    <div>
      <AppBarHeader 
        isAdmin={isAdmin} 
        userId={userId} 
        onLogout={handleLogout}
      />
      <Box sx={{ maxWidth: 1000, margin: '0 auto', padding: '20px', textAlign: 'center' }}>
        <Typography variant="h4" sx={{ marginTop: 4, marginBottom: 2 }}>
          Deleted Users
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Button
          variant="outlined"
          onClick={() => navigate('/admin')}
          sx={{ mb: 2 }}
        >
          ← Back to Admin Dashboard
        </Button>
        {deletedUsers.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No deleted users found.
          </Alert>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Username</TableCell>
                <TableCell>Email Address</TableCell>
                <TableCell>Admin</TableCell>
                <TableCell>Disabled</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deletedUsers.map((user) => (
                <TableRow key={user._id}>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.isAdmin ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{user.isDisabled ? 'Yes' : 'No'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Restore User">
                      <IconButton
                        color="primary"
                        onClick={() => handleRestoreUser(user._id)}
                        sx={{ marginRight: 1 }}
                      >
                        <RestoreIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Permanently Delete User">
                      <IconButton
                        color="error"
                        onClick={() => handlePermanentDelete(user._id)}
                      >
                        <DeleteForeverIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
    </div>
  );
}

export default DeletedUsers;
